import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtEUR, mediaUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Check, Minus, Plus, ArrowRight, ShoppingCart, MessageCircle } from "lucide-react";

const STEPS = ["Tubi", "Hylsa", "Kopling A", "Kopling B", "Gjatësia", "Përmbledhja"];

export default function Configurator() {
  const [step, setStep] = useState(1);
  const [data, setData] = useState({ hoses: [], couplings: [], ferrules: [], sizes: [], assembly_price_per_end: 2.5 });
  const [settings, setSettings] = useState({ whatsapp_number: "" });
  const [size, setSize] = useState("3/8\"");
  const [hose, setHose] = useState(null);
  const [ferrule, setFerrule] = useState(null);
  const [couplingA, setCouplingA] = useState(null);
  const [couplingB, setCouplingB] = useState(null);
  const [length, setLength] = useState(1);
  const [pieces, setPieces] = useState(1);
  const [assembly, setAssembly] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState(null); // null | "delivery" | "pickup"
  const { addToCart } = useApp();
  const nav = useNavigate();

  useEffect(() => { api.get("/configurator/data").then(r => setData(r.data)); }, []);
  useEffect(() => { api.get("/settings").then(r => setSettings(r.data)).catch(() => {}); }, []);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  // when hose or size changes, reset dependent selections
  useEffect(() => { setFerrule(null); setCouplingA(null); setCouplingB(null); }, [hose?.id, size]);

  const filteredFerrules = data.ferrules.filter(f => {
    if (f.size !== size) return false;
    if (!f.hose_type || !hose) return true;
    // when hose is from products, ignore legacy hose_type filter (product-sourced hoses don't have legacy codes)
    if (data.source?.hose === "products" || data.source?.hose === "role") return true;
    return f.hose_type === hose.code;
  });
  const filteredCouplings = data.couplings.filter(c => c.size === size);
  const filteredHoses = data.hoses.filter(h => !h.size || h.size === size);

  const hosePrice = hose ? hose.price_per_meter * length : 0;
  const fPrice = ferrule ? ferrule.price * 2 : 0; // one on each end
  const aPrice = couplingA?.price || 0;
  const bPrice = couplingB?.price || 0;
  // Assembly only counts when both couplings are chosen (nothing to assemble otherwise)
  // Assembly only counts when explicitly selected AND both couplings are chosen
  const asmPrice = (assembly === true && couplingA && couplingB) ? data.assembly_price_per_end * 2 : 0;
  const unitTotal = hosePrice + fPrice + aPrice + bPrice + asmPrice;
  const itemsTotal = unitTotal * pieces;
  const delivery = deliveryMethod === "delivery" ? 5 : 0;
  const total = itemsTotal + delivery;

  const canNext =
    (step === 1 && hose) ||
    (step === 2 && ferrule) ||
    (step === 3 && couplingA) ||
    (step === 4 && couplingB) ||
    (step === 5 && length > 0 && pieces > 0);

  const addConfiguredToCart = () => {
    const cfg = {
      hose: `${hose.code} – ${size}`,
      ferrule: ferrule.code,
      couplingA: couplingA.name,
      couplingB: couplingB.name,
      length_m: length,
      assembly,
    };
    addToCart({
      product_id: null,
      name: `Tub hidraulik ${hose.code} ${size} ${length}m`,
      configuration: cfg,
      quantity: pieces, unit_price: unitTotal,
      image: hose.image,
    });
    toast.success("Konfigurimi u shtua në shportë");
    nav("/shporta");
  };

  const sendWhatsApp = () => {
    const phone = (settings.whatsapp_number || "").replace(/\D/g, "");
    if (!phone) { toast.error("Numri i WhatsApp nuk është konfiguruar"); return; }
    const lines = [
      "*Kërkesë për ofertë - Konfigurim tubi HidroGV*",
      "",
      `• Tubi: ${hose.code} – ${size}`,
      `• Hylsa: ${ferrule.code}`,
      `• Kopling A: ${couplingA.name}`,
      `• Kopling B: ${couplingB.name}`,
      `• Gjatësia: ${length} m`,
      `• Copët: ${pieces}`,
      `• Montimi: ${assembly === true ? "Po, me presim" : "Jo, pa montim"}`,
      `• Marrja: ${deliveryMethod === "delivery" ? "Dërgesa (Posta) " + fmtEUR(delivery) : "Vjen e merr (0 €)"}`,
      "",
      `*TOTALI: ${fmtEUR(total)}*`,
      "",
      "Ju lutem më kthejeni ofertë. Faleminderit!",
    ];
    const msg = encodeURIComponent(lines.join("\n"));
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
      {/* Progress */}
      <div className="grid grid-cols-6 gap-1 mb-6">
        {STEPS.map((s, i) => {
          const n = i + 1;
          const done = n < step;
          const active = n === step;
          return (
            <div key={s} className="flex flex-col items-center gap-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${active ? "bg-[#FFD600] text-black" : done ? "bg-emerald-600 text-white" : "bg-[#262626] text-neutral-400"}`}>
                {done ? <Check size={14} /> : n}
              </div>
              <div className={`text-[8px] sm:text-[10px] uppercase tracking-wider text-center leading-tight ${active ? "text-[#FFD600]" : "text-neutral-500"}`}>{s}</div>
            </div>
          );
        })}
      </div>

      {/* STEP 1: Hose (Tubi) */}
      {step === 1 && (
        <div className="hg-card p-4 sm:p-6">
          <div className="font-display text-2xl uppercase mb-1">1. Zgjidh tubin</div>
          <div className="text-sm text-neutral-400 mb-4">Zgjidh diametrin dhe llojin e tubit</div>
          <div className="mb-4">
            <label className="text-xs uppercase text-neutral-400 tracking-wider">Diametri</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {data.sizes.map(s => (
                <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`}
                  className={`px-3 py-2 border text-sm ${size === s ? "border-[#FFD600] bg-[#FFD600] text-black font-bold" : "border-[#1F2B44] hover:border-[#FFD600]"}`}>{s}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {filteredHoses.length === 0 ? (
              <div className="col-span-2 md:col-span-4 text-center py-6 text-neutral-400 text-sm">
                Nuk ka gypa për madhësinë {size}. Ndrysho madhësinë ose shto produkte në kategorinë e lidhur.
              </div>
            ) : filteredHoses.map(h => {
              const inStock = (h.stock_qty ?? 1) > 0;
              return (
                <button key={h.id} onClick={() => setHose(h)} data-testid={`hose-${h.code}`}
                  className={`text-left hg-card p-3 ${hose?.id === h.id ? "border-[#FFD600] ring-2 ring-[#FFD600]/40" : ""}`}>
                  <div className="aspect-square bg-[#131E30] overflow-hidden mb-3 flex items-center justify-center">
                    {h.image ? <img src={mediaUrl(h.image)} alt={h.code} className="w-full h-full object-cover" />
                      : <div className="text-neutral-600 text-xs">Pa foto</div>}
                  </div>
                  <div className="font-display text-xl">{h.code}</div>
                  {h.size && <div className="text-xs text-neutral-400 mt-1">Madhësia: {h.size}</div>}
                  {h.pressure_bar > 0 && <div className="text-xs text-neutral-400">Presion: {h.pressure_bar} bar</div>}
                  {h.standard && <div className="text-xs text-neutral-400">Standardi: {h.standard}</div>}
                  <div className="text-[#FFD600] font-bold mt-2">{fmtEUR(h.price_per_meter)}/m</div>
                  <div className={`text-[10px] mt-1 ${inStock ? "text-emerald-500" : "text-red-500"}`}>● {inStock ? "Në stok" : "S'ka në stok"}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* STEP 2: Ferrule (Hylsa) */}
      {step === 2 && (
        <div className="hg-card p-4 sm:p-6">
          <div className="font-display text-2xl uppercase mb-1">2. Zgjidh hylsën</div>
          <div className="text-sm text-neutral-400 mb-4">Hylsa (kollari) që shtrëngohet mbi gyp — 2 copë (një për anë)</div>
          {filteredFerrules.length === 0 ? (
            <div className="text-center py-8 text-neutral-400 text-sm">Nuk ka hylsa të disponueshme për këtë gyp/madhësi. Kontakto administratorin.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {filteredFerrules.map(f => (
                <button key={f.id} onClick={() => setFerrule(f)} data-testid={`ferrule-${f.code}`}
                  className={`text-left hg-card p-3 ${ferrule?.id === f.id ? "border-[#FFD600] ring-2 ring-[#FFD600]/40" : ""}`}>
                  <div className="aspect-square bg-[#131E30] overflow-hidden mb-2">
                    {f.image && <img src={mediaUrl(f.image)} alt={f.code} className="w-full h-full object-cover" />}
                  </div>
                  <div className="font-medium text-sm">{f.code}</div>
                  <div className="text-xs text-neutral-400 mt-1">Madhësia: {f.size}{f.hose_type ? ` • ${f.hose_type}` : ""}</div>
                  <div className="text-[#FFD600] font-bold mt-1">{fmtEUR(f.price)}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* STEP 3/4: Couplings */}
      {(step === 3 || step === 4) && (
        <div className="hg-card p-4 sm:p-6">
          <div className="font-display text-2xl uppercase mb-1">{step}. Kopling {step === 3 ? "A" : "B"}</div>
          <div className="text-sm text-neutral-400 mb-4">Zgjidh koplingun {step === 3 ? "A" : "B"} për tubin {size}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {filteredCouplings.map(c => {
              const selected = step === 3 ? couplingA?.id === c.id : couplingB?.id === c.id;
              return (
                <button key={c.id} onClick={() => step === 3 ? setCouplingA(c) : setCouplingB(c)}
                  data-testid={`coupling-${step}-${c.id}`}
                  className={`text-left hg-card p-3 ${selected ? "border-[#FFD600] ring-2 ring-[#FFD600]/40" : ""}`}>
                  <div className="aspect-square bg-[#131E30] overflow-hidden mb-2">
                    {c.image && <img src={mediaUrl(c.image)} alt={c.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="font-medium text-sm">{c.name}</div>
                  <div className="text-[#FFD600] font-bold mt-1">{fmtEUR(c.price)}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* STEP 5: Length */}
      {step === 5 && (
        <div className="hg-card p-4 sm:p-6 space-y-6">
          <div>
            <div className="font-display text-2xl uppercase mb-1">5. Gjatësia & Copët</div>
            <div className="text-sm text-neutral-400">Përcakto gjatësinë dhe numrin e copëve</div>
          </div>
          <div>
            <label className="text-xs uppercase text-neutral-400 tracking-wider">Gjatësia (metra)</label>
            <div className="flex items-center gap-3 mt-2">
              <button onClick={() => setLength(Math.max(0.5, +(length - 0.5).toFixed(2)))} data-testid="len-minus" className="w-12 h-12 border border-[#1F2B44] hover:bg-[#262626] flex items-center justify-center"><Minus /></button>
              <input type="number" step="0.1" value={length} onChange={e => setLength(+e.target.value)} data-testid="len-input"
                className="w-24 h-12 bg-[#131E30] border border-[#1F2B44] text-center text-lg font-bold" />
              <span className="text-neutral-400">m</span>
              <button onClick={() => setLength(+(length + 0.5).toFixed(2))} data-testid="len-plus" className="w-12 h-12 border border-[#1F2B44] hover:bg-[#262626] flex items-center justify-center"><Plus /></button>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase text-neutral-400 tracking-wider">Copët (sasia)</label>
            <div className="flex items-center gap-3 mt-2">
              <button onClick={() => setPieces(Math.max(1, pieces - 1))} data-testid="pcs-minus" className="w-12 h-12 border border-[#1F2B44] hover:bg-[#262626] flex items-center justify-center"><Minus /></button>
              <input type="number" value={pieces} onChange={e => setPieces(Math.max(1, +e.target.value))} data-testid="pcs-input"
                className="w-24 h-12 bg-[#131E30] border border-[#1F2B44] text-center text-lg font-bold" />
              <span className="text-neutral-400">copë</span>
              <button onClick={() => setPieces(pieces + 1)} data-testid="pcs-plus" className="w-12 h-12 border border-[#1F2B44] hover:bg-[#262626] flex items-center justify-center"><Plus /></button>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase text-neutral-400 tracking-wider">Montimi i koplingave</label>
            <div className="flex gap-2 mt-2">
              <button onClick={() => setAssembly(true)} data-testid="asm-yes" className={`px-4 py-3 border text-sm ${assembly === true ? "border-[#FFD600] bg-[#FFD600] text-black font-bold" : "border-[#1F2B44]"}`}>Po, me presim (+5€)</button>
              <button onClick={() => setAssembly(false)} data-testid="asm-no" className={`px-4 py-3 border text-sm ${assembly === false ? "border-[#FFD600] bg-[#FFD600] text-black font-bold" : "border-[#1F2B44]"}`}>Jo, pa montim</button>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase text-neutral-400 tracking-wider">Mënyra e marrjes</label>
            <div className="flex gap-2 mt-2 flex-wrap">
              <button onClick={() => setDeliveryMethod("delivery")} data-testid="delivery-yes" className={`px-4 py-3 border text-sm ${deliveryMethod === "delivery" ? "border-[#FFD600] bg-[#FFD600] text-black font-bold" : "border-[#1F2B44]"}`}>Dërgesa (+5.00 €)</button>
              <button onClick={() => setDeliveryMethod("pickup")} data-testid="delivery-no" className={`px-4 py-3 border text-sm ${deliveryMethod === "pickup" ? "border-[#FFD600] bg-[#FFD600] text-black font-bold" : "border-[#1F2B44]"}`}>Vjen e merr (0 €)</button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 6: Summary */}
      {step === 6 && (
        <div className="hg-card p-4 sm:p-6">
          <div className="font-display text-2xl uppercase mb-4">6. Përmbledhja</div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm mb-6">
            <div className="text-neutral-400 uppercase text-xs">Tubi</div><div>{hose?.code} – {size}</div>
            <div className="text-neutral-400 uppercase text-xs">Hylsa</div><div>{ferrule?.code}</div>
            <div className="text-neutral-400 uppercase text-xs">Kopling A</div><div>{couplingA?.name}</div>
            <div className="text-neutral-400 uppercase text-xs">Kopling B</div><div>{couplingB?.name}</div>
            <div className="text-neutral-400 uppercase text-xs">Gjatësia</div><div>{length} m</div>
            <div className="text-neutral-400 uppercase text-xs">Copët</div><div>{pieces}</div>
            <div className="text-neutral-400 uppercase text-xs">Montimi</div><div>{assembly === true ? "Po, me presim" : assembly === false ? "Jo, pa montim" : "—"}</div>
            <div className="text-neutral-400 uppercase text-xs">Marrja</div><div>{deliveryMethod === "delivery" ? "Dërgesa" : deliveryMethod === "pickup" ? "Vjen e merr" : "—"}</div>
          </div>
          <div className="border-t border-[#1F2B44] pt-4 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-neutral-400">Gypi ({length}m × {fmtEUR(hose?.price_per_meter || 0)}/m)</span><span>{fmtEUR(hosePrice)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-400">Hylsa (×2)</span><span>{fmtEUR(fPrice)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-400">Kopling A</span><span>{fmtEUR(aPrice)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-400">Kopling B</span><span>{fmtEUR(bPrice)}</span></div>
            {asmPrice > 0 && <div className="flex justify-between"><span className="text-neutral-400">Montimi me presë (2 anët)</span><span>{fmtEUR(asmPrice)}</span></div>}
            {pieces > 1 && <div className="flex justify-between border-t border-[#1F2B44] pt-2"><span className="text-neutral-400">Nëntotali për {pieces} copë</span><span>{fmtEUR(itemsTotal)}</span></div>}
            {deliveryMethod && <div className="flex justify-between"><span className="text-neutral-400">{deliveryMethod === "delivery" ? "Dërgesa (Posta)" : "Vjen e merr"}</span><span>{fmtEUR(delivery)}</span></div>}
            <div className="flex justify-between font-display text-2xl pt-2 border-t border-[#1F2B44]">
              <span>TOTALI</span><span className="text-[#FFD600]">{fmtEUR(total)}</span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mt-6">
            <button onClick={addConfiguredToCart} data-testid="cfg-add-cart" className="hg-btn-primary flex-1 py-4 uppercase text-sm inline-flex items-center justify-center gap-2">
              <ShoppingCart size={18} /> Shto në shportë
            </button>
            <button onClick={() => { addConfiguredToCart(); nav("/checkout"); }} data-testid="cfg-order"
              className="border-2 border-white py-4 px-6 uppercase text-sm hover:bg-white hover:text-black transition-colors">
              Porosit Tani
            </button>
          </div>
          {settings.whatsapp_number && (
            <button onClick={sendWhatsApp} data-testid="cfg-whatsapp"
              className="mt-2 w-full py-4 uppercase text-sm inline-flex items-center justify-center gap-2 bg-[#25D366] text-white hover:bg-[#1ea952] transition-colors font-bold">
              <MessageCircle size={18} /> Dërgo në WhatsApp për ofertë
            </button>
          )}
        </div>
      )}

      {/* Sticky bottom summary + nav */}
      <div className="mt-6 hg-card p-4 flex flex-col sm:flex-row items-center gap-3">
        <div className="flex-1 text-sm">
          {hose && <span className="text-neutral-400">Tubi: <span className="text-white font-medium">{hose.code} {size}</span> • </span>}
          <span className="text-[#FFD600] font-display text-2xl">{fmtEUR(total)}</span>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          {step > 1 && <button onClick={() => setStep(step - 1)} data-testid="cfg-back" className="border border-[#1F2B44] px-4 py-3 uppercase text-xs flex-1 sm:flex-none">Prapa</button>}
          {step < 6 && (
            <button onClick={() => canNext && setStep(step + 1)} disabled={!canNext} data-testid="cfg-next"
              className={`px-6 py-3 uppercase text-xs flex-1 sm:flex-none inline-flex items-center justify-center gap-1 ${canNext ? "hg-btn-primary" : "bg-neutral-800 text-neutral-500 cursor-not-allowed"}`}>
              Vazhdo <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
